import { Select, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { AggFuncSubForm } from 'pages/revisions-module/template-editor/editors/form/configurator/properties/AggFuncSubForm';
import { useFormSections } from 'pages/revisions-module/template-editor/editors/form/section/FormSectionsDataProvider';
import {
  IFormInputSpec,
  IFormInputSpecMeasurementColumnAggregator,
} from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo } from 'react';

/**
 * Sub-form for measurement column aggregator input properties.
 */
export default function MeasurementColumnAggregatorInputPropertiesSubForm({
  form,
}: {
  form: UseFormReturnType<IFormInputSpec>;
}) {
  const { sections } = useFormSections();

  const measurementSections = useMemo(() => sections.filter(({ tab }) => tab === 'measurements'), [sections]);

  const sectionOptions = useMemo(
    () => measurementSections.map(({ id, name }) => ({ value: id, label: name || '(Bez názvu)' })),
    [measurementSections]
  );

  const spec = form.values as IFormInputSpecMeasurementColumnAggregator;
  const selectedSection = useMemo(
    () => measurementSections.find(({ id }) => id === spec.sectionId),
    [measurementSections, spec.sectionId]
  );

  const inputOptions = useMemo(
    () =>
      (selectedSection?.rows ?? [])
        .flatMap(({ inputs }) => inputs)
        .map(({ spec }) => ({ value: spec.name, label: spec.label })),
    [selectedSection]
  );

  const selectedInputSpec = useMemo(
    () =>
      (selectedSection?.rows ?? [])
        .flatMap(({ inputs }) => inputs)
        .find(({ spec: inputSpec }) => inputSpec.name === spec.inputName)?.spec,
    [selectedSection, spec.inputName]
  );

  const selectedValueOptions = useMemo(
    () => (selectedInputSpec?.type === 'select' ? selectedInputSpec.options : undefined),
    [selectedInputSpec]
  );

  useEffect(() => {
    if (spec.inputName && !inputOptions.some(({ value }) => value === spec.inputName)) {
      form.setFieldValue('inputName', inputOptions[0]?.value || '');
    }
  }, [spec.inputName, inputOptions, form]);

  useEffect(() => {
    if (spec.aggFunc !== 'count-selected') {
      return;
    }

    if (selectedValueOptions && !selectedValueOptions.includes(spec.selectedValue || '')) {
      form.setFieldValue('selectedValue', selectedValueOptions[0] || '');
    }
  }, [spec.aggFunc, spec.selectedValue, selectedValueOptions, form]);

  return (
    <Stack spacing={12}>
      <Select
        required
        size="sm"
        label="Tabuľka meraní"
        data={sectionOptions}
        nothingFound="Žiadna tabuľka"
        {...form.getInputProps('sectionId')}
      />
      <Select
        required
        size="sm"
        label="Stĺpec"
        data={inputOptions}
        nothingFound="Žiadny stĺpec"
        disabled={inputOptions.length === 0}
        {...form.getInputProps('inputName')}
      />
      <AggFuncSubForm form={form} selectedValueOptions={selectedValueOptions} />
    </Stack>
  );
}
